import { useReactiveVar } from '@apollo/client';
import { NotificationGray } from '@components/common/Notifications';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { BaseText, Meta, theme } from 'aimo-ui';
import SkeletonLoaderRow, {
  SkeletonWrapper,
} from 'components/loading/SkeletonLoaderRow';
import FlatListCustom from 'components/sheets/FlatListCustom';
import AccountContext from 'context/AccountContext';
import useCustomBottomSheet from 'hooks/useCustomBottomSheet';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import { useGetParkingZonesWithBuyablePermits } from 'hooks/useGetParkingZonesWithBuyablePermits';
import useParkZonesByDistance from 'hooks/useParkZonesByDistance';
import useStableLocation from 'hooks/useStableLocation';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { featureCountryCode } from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { useMapStore } from 'stores/mapStore';
import { useUiStore } from 'stores/uiStore';
import { Divider } from 'styles/CommonLayoutStyles';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { ParkZone } from 'types/generatedSchemaTypes';
import { checkLocationPermissions } from 'utils/mapUtils';
import { isOnboardingOpen } from 'utils/navigationUtils';
import {
  parkMapParkzoneFilter,
  quickMenuSort,
  sortInMainMapViewByZoneAndBenefit,
} from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import SearchItem from './SearchItem';
import SearchItemSkeleton from './SearchItemSkeleton';
import { Center, Container, MarginView, Row } from './styles/styles';

const ListFooter = () => <MarginView />;

const ItemSeparator = () => <Divider marginVertical={10} />;

type ParkingZoneListProps = {
  testID?: string;
  withLTP?: boolean;
};

const ParkingZonesByDistance = ({
  testID,
  withLTP = false,
}: ParkingZoneListProps) => {
  const navigation: NavigationProp<any> = useNavigation();
  const { t } = useTranslation();
  const currentLocation = useMapStore((state) => state.userLocation);
  const appCountry = useReactiveVar(featureCountryCode);
  const { getParkZones, parkZones } = useParkZonesByDistance();
  const {
    customer: { householdBenefits },
  } = useContext(AccountContext);
  const { snapToIndex, animatedIndex } = useCustomBottomSheet();
  const [hasSnapped, setHasSnapped] = useState(false);
  const { isLoggedIn } = useIsLoggedIn();
  const modalShown = useUiStore((state) => state.locationModalShown);
  const updateScreenOpen = useUiStore((state) => state.isAppStartupScreenOpen);
  const currentAppCountry = useAuthStore(
    (state) => state.currentCustomerCountry
  );
  const isAccountRestricted = useAuthStore((state) => state.accountRestricted);
  // The location the list is sorted and fetched by. It only changes when the user has moved more
  // than 100 metres.
  const stableLocation = useStableLocation(currentLocation);
  const {
    parkingZonesData: parkZonesLTPData,
    loading: parkZonesLTPLoading,
    error: parkZonesLTPError,
  } = useGetParkingZonesWithBuyablePermits(
    currentAppCountry,
    withLTP,
    stableLocation
  );
  const selectedQuickAccessItem = useUiStore(
    (state) => state.selectedParkScreenQuickAccessItem
  );

  const results = useMemo(() => {
    const parkZonesData = parkZones?.data?.parkZonesByDistance?.content ?? [];
    const filteredParkzones = parkMapParkzoneFilter(
      parkZonesData as ParkZone[],
      householdBenefits,
      undefined,
      false
    );

    if (withLTP) {
      return parkZonesLTPData;
    }

    return [...filteredParkzones]
      .slice()
      .sort((a, b) =>
        quickMenuSort(a, b, selectedQuickAccessItem, currentLocation, withLTP)
      )
      .slice()
      .sort((a, b) =>
        sortInMainMapViewByZoneAndBenefit(a, b, householdBenefits)
      );
  }, [
    parkZones?.data?.parkZonesByDistance?.content,
    parkZonesLTPData,
    selectedQuickAccessItem,
    withLTP,
    currentLocation,
    householdBenefits,
  ]);

  const updateCurrentCountryCode = useCallback((zones: ParkZone[]) => {
    const { countryCode } = zones[0] || {};
    if (countryCode) {
      useAuthStore.getState().setCurrentCustomerCountry(countryCode);
    }
    return countryCode;
  }, []);
  const checkAppCountry = useCallback(
    (zones: ParkZone[]) => {
      const currentCountry = updateCurrentCountryCode(zones);
      if (
        currentCountry &&
        currentCountry !== appCountry &&
        !modalShown &&
        !isOnboardingOpen(navigation) &&
        !isAccountRestricted &&
        !useUiStore.getState().isApproveTermsOpen
      ) {
        useUiStore.getState().setSheetModal('parkingAppLocation');
        useUiStore.getState().setLocationModalShown(true);
      }
    },
    [
      updateCurrentCountryCode,
      appCountry,
      modalShown,
      navigation,
      isAccountRestricted,
    ]
  );

  useEffect(() => {
    if (isLoggedIn && !updateScreenOpen) {
      const timer = setTimeout(() => {
        checkAppCountry(results as ParkZone[]);
      }, 500);
      return () => clearTimeout(timer);
    } else if (!currentAppCountry) {
      updateCurrentCountryCode(results as ParkZone[]);
    }
  }, [
    results,
    isLoggedIn,
    updateScreenOpen,
    checkAppCountry,
    currentAppCountry,
    updateCurrentCountryCode,
  ]);

  useEffect(() => {
    if (!hasSnapped && animatedIndex.value === 0 && !withLTP) {
      setTimeout(() => {
        snapToIndex(1);
      }, 500);
      setHasSnapped(true);
    }
  }, [snapToIndex, animatedIndex.value, hasSnapped, withLTP]);

  useEffect(() => {
    if (!withLTP && stableLocation) {
      getParkZones({
        variables: {
          coordinates: stableLocation,
          size: 10,
        },
      });
    }
  }, [withLTP, stableLocation, getParkZones]);

  const error = withLTP ? parkZonesLTPError : parkZones?.error;
  // Without a location the list shows the notice to enable it, not a loading state
  const loading = withLTP
    ? Boolean(currentLocation) && parkZonesLTPLoading
    : parkZones?.loading;

  if (loading) {
    return (
      <>
        <Row mt={15} mb={20}>
          <SkeletonWrapper width={80}>
            <SkeletonLoaderRow height={15} />
          </SkeletonWrapper>
        </Row>
        <SearchItemSkeleton />
        <SearchItemSkeleton />
        <SearchItemSkeleton />
        <SearchItemSkeleton />
      </>
    );
  }

  if (error && !parkZones) {
    return (
      <Container testID={testID}>
        <Center>
          <Meta>{t('parkingZone.errorInLoading')}</Meta>
        </Center>
      </Container>
    );
  }

  if (!loading && !currentLocation) {
    return (
      <Container testID={testID}>
        <NotificationGray
          label={t('parkingZone.enableLocationServices')}
          action={checkLocationPermissions}
          text={t('parkingZone.seeWhereYouAreOnTheMap')}
          buttonText={t('button.enable')}
        />
      </Container>
    );
  }

  return (
    <Container testID={testID}>
      <ColumnWithMargins mb={16}>
        <Meta
          {...testIdentifiers(`${testID}-subtitle`)}
          color={theme.colors.gray300}>
          {t(
            withLTP
              ? 'parkingZone.closestLTPsToYou'
              : 'parkingZone.closestToYou'
          )}
        </Meta>
      </ColumnWithMargins>

      {results &&
        (results.length > 0 ? (
          <FlatListCustom
            ItemSeparatorComponent={ItemSeparator}
            data={results as ParkZone[]}
            removeClippedSubviews={false}
            {...testIdentifiers(`${testID}-flatlist`)}
            renderItem={({ item }) => (
              <SearchItem
                item={item}
                currentLocation={currentLocation}
                withLTP={withLTP}
              />
            )}
            ListFooterComponent={ListFooter}
            keyExtractor={(item, index) =>
              item?.uid ? String(item.uid) : `zone-${index}}`
            }
          />
        ) : (
          <Center>
            <BaseText>{t('parkingZone.noNearbyFound')}</BaseText>
          </Center>
        ))}
    </Container>
  );
};

export default ParkingZonesByDistance;
